// Layout da marca d'agua: transforma o design normalizado + dados da oferta em
// operacoes de desenho em PIXELS. Puro e deterministico: o renderizador do bot
// so desenha o que sai daqui, entao previa e envio nunca divergem.
import { buildTemplateVars } from "../forwarding/index.js";
import { WATERMARK_CANVAS_SIZE, WATERMARK_TEXT_VARIABLES, } from "./types.js";
const VAR_RE = new RegExp(`\\{(${WATERMARK_TEXT_VARIABLES.join("|")})\\}`, "g");
function present(value) {
    return value !== null && value.trim().length > 0;
}
/** Substitui {title}, {price}... Se o texto usa variaveis e TODAS estao
 *  ausentes, devolve "" (ex.: selo "-{discountPercent}" sem desconto some). */
export function resolveWatermarkText(template, data) {
    const names = [...template.matchAll(VAR_RE)].map((m) => m[1]);
    if (names.length > 0 && !names.some((name) => present(data[name]))) {
        return "";
    }
    return template
        .replace(VAR_RE, (_, name) => data[name] ?? "")
        .split("\n")
        .map((line) => line.replace(/[ \t]+/g, " ").trim())
        .filter((line) => line.length > 0)
        .join("\n");
}
/** Variaveis de texto a partir da oferta extraida (mesmas do template de texto). */
export function watermarkTextData(offer) {
    if (!offer) {
        return { title: null, price: null, originalPrice: null, discountPercent: null, coupon: null };
    }
    const vars = buildTemplateVars(offer, null, null);
    return {
        title: vars.title,
        price: vars.price,
        originalPrice: vars.originalPrice,
        discountPercent: vars.discountPercent,
        coupon: vars.coupon,
    };
}
/** Fracao (0..1) para pixels, sempre dentro da tela e com lado >= 1. */
export function toPxRect(box, size) {
    const x = Math.min(size - 1, Math.max(0, Math.round(box.x * size)));
    const y = Math.min(size - 1, Math.max(0, Math.round(box.y * size)));
    return {
        x,
        y,
        w: Math.max(1, Math.min(size - x, Math.round(box.w * size))),
        h: Math.max(1, Math.min(size - y, Math.round(box.h * size))),
    };
}
function bandHeight(band, size) {
    return band.enabled ? Math.round(band.size * size) : 0;
}
/** Camada -> operacao de desenho. `toRect` converte a caixa normalizada para
 *  pixels da tela de destino (quadrada na marca d'agua, 9:16 nos stories). */
export function layerToOp(layer, data, toRect, hasLogo) {
    const rect = toRect(layer.box);
    const rotation = layer.rotation;
    if (layer.kind === "logo") {
        return hasLogo ? { kind: "logo", rect, rotation, opacity: layer.opacity } : null;
    }
    const text = resolveWatermarkText(layer.text, data);
    if (text.length === 0)
        return null;
    if (layer.kind === "badge") {
        return {
            kind: "badge",
            rect,
            rotation,
            text,
            color: layer.color,
            textColor: layer.textColor,
            shape: layer.shape,
        };
    }
    if (layer.kind === "contacts") {
        return {
            kind: "text",
            rect,
            rotation,
            text,
            color: layer.color,
            background: layer.background,
            align: "center",
            bold: false,
        };
    }
    return {
        kind: "text",
        rect,
        rotation,
        text,
        color: layer.color,
        background: layer.background,
        align: layer.align,
        bold: layer.bold,
    };
}
function bandOp(band, y, h, size, data) {
    return {
        kind: "band",
        rect: { x: 0, y, w: size, h },
        color: band.color,
        text: resolveWatermarkText(band.text, data),
        textColor: band.textColor,
    };
}
/** Operacoes de desenho na ordem: faixas, depois camadas (ordem do usuario). */
export function computeWatermarkLayout(design, data, options) {
    const size = options.size ?? WATERMARK_CANVAS_SIZE;
    const topH = bandHeight(design.topBand, size);
    const bottomH = bandHeight(design.bottomBand, size);
    const bands = [];
    if (topH > 0)
        bands.push(bandOp(design.topBand, 0, topH, size, data));
    if (bottomH > 0)
        bands.push(bandOp(design.bottomBand, size - bottomH, bottomH, size, data));
    const layers = design.layers
        .map((layer) => layerToOp(layer, data, (box) => toPxRect(box, size), options.hasLogo))
        .filter((op) => op !== null);
    return {
        size,
        background: design.photo.background,
        photoRect: { x: 0, y: topH, w: size, h: Math.max(1, size - topH - bottomH) },
        photoFit: design.photo.fit,
        ops: [...bands, ...layers],
    };
}
//# sourceMappingURL=layout.js.map